/**
 * ipi.js - International Prognostic Index (IPI) for the standalone IPI
 * Calculator (client feedback #13, Oct 2026).
 *
 * Same rules as the NHL planner (planner/legacy/engine.js: ipiAge ... computeIPI,
 * and computePersona's grouping): one point each for age > 60, Ann Arbor stage
 * III or IV, LDH > 1 x ULN, ECOG >= 2 and more than one extranodal site. Total
 * 0-5; Low Risk 0-2, High Risk 3-5. Boundary values score 0: age 60,
 * LDH 1 x ULN, ECOG 1, one extranodal site.
 *
 * Inputs are the raw field strings. Nothing here is stored anywhere.
 */
export const IPI_STAGES = ['I', 'II', 'III', 'IV'];
export const IPI_ECOG = ['0', '1', '2', '3', '4'];

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());
const isWhole = (s) => /^\d+$/.test(s);                 // 0, 1, 2 ... (no sign, no decimals)
const isNonNegative = (s) => /^\d+(\.\d+)?$/.test(s);   // 0, 1.5 ... (no sign)

export const IPI_FIELDS = [
  {
    key: 'age', label: 'Age', unit: 'years', rule: 'Age > 60',
    check: (s) => (isWhole(s) && Number(s) <= 120 ? null : 'Enter the age in whole years, 0 to 120.'),
    point: (s) => Number(s) > 60,
  },
  {
    key: 'stage', label: 'Ann Arbor stage', unit: '', rule: 'Stage III or IV',
    check: (s) => (IPI_STAGES.includes(s) ? null : 'Choose stage I, II, III or IV.'),
    point: (s) => s === 'III' || s === 'IV',
  },
  {
    key: 'ldh', label: 'LDH', unit: '× ULN', rule: 'LDH > 1 × ULN',
    check: (s) => (isNonNegative(s) ? null : 'Enter LDH as a multiple of the upper limit of normal, 0 or more (for example 1.5).'),
    point: (s) => Number(s) > 1,
  },
  {
    key: 'ecog', label: 'ECOG performance status', unit: '', rule: 'ECOG ≥ 2',
    check: (s) => (IPI_ECOG.includes(s) ? null : 'Choose an ECOG score from 0 to 4.'),
    point: (s) => Number(s) >= 2,
  },
  {
    key: 'extranodal', label: 'Extranodal sites', unit: 'sites', rule: 'More than 1 site',
    check: (s) => (isWhole(s) ? null : 'Enter the number of involved sites as a whole number, 0 or more.'),
    point: (s) => Number(s) > 1,
  },
];

/**
 * values: { age, stage, ldh, ecog, extranodal } as entered.
 * Returns { complete, errors } and, once all five are valid,
 * { score, risk, breakdown: [{ key, label, rule, value, point }] }.
 */
export function calculateIpi(values) {
  const errors = {};
  let filled = 0;
  for (const f of IPI_FIELDS) {
    const s = text(values[f.key]);
    if (!s) continue;
    filled += 1;
    const e = f.check(s);
    if (e) errors[f.key] = e;
  }
  if (filled < IPI_FIELDS.length || Object.keys(errors).length) return { complete: false, errors };
  const breakdown = IPI_FIELDS.map((f) => {
    const s = text(values[f.key]);
    return { key: f.key, label: f.label, rule: f.rule, value: s, point: f.point(s) ? 1 : 0 };
  });
  const score = breakdown.reduce((sum, b) => sum + b.point, 0);
  return { complete: true, errors, score, risk: score <= 2 ? 'Low Risk' : 'High Risk', breakdown };
}
